
import { useEffect, useState, useRef, useMemo } from "react";
import socket from "../utils/socket";
import { fetchUserChat, sendCallEvent } from "../utils/api";
import useWebRTC from "./useWebRTC";

export default function useChatLogic({ user, toId, token }) {
  // --- States ---
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [typing, setTyping] = useState(false);
  const [history, setHistory] = useState([]);

  // Call States
  const [incomingCall, setIncomingCall] = useState(null);
  const [inCall, setInCall] = useState(false);
  const [calling, setCalling] = useState(false);
  const [callType, setCallType] = useState("video");
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [showProfile, setShowProfile] = useState(false);

  // --- Refs ---
  const chatRef = useRef(null);
  const ringtoneRef = useRef(null);
  const callTypeRef = useRef("video");
  const callStartTimeRef = useRef(null);
  const missedCallTimerRef = useRef(null);
  const callConnectedRef = useRef(false);
  const activePeerIdRef = useRef(null);
  const callLoggedRef = useRef(false);
  const joinedRoomRef = useRef(null);

  const myId = user?._id || user?.id;

  // --- WebRTC Hook ---
  const {
    startWebRTC,
    endWebRTC,
    applyAnswer,
    applyRemoteIce,
  } = useWebRTC({ selfId: myId, setInCall, setLocalStream, setRemoteStream });

  // --- Helpers ---
  const playRingtone = () => {
    if (!ringtoneRef.current) {
      const audio = new Audio("/calling.mp3");
      audio.loop = true;
      audio.play().catch(() => {});
      ringtoneRef.current = audio;
    }
  };

  const stopRingtone = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current = null;
    }
  };


// 2️⃣ Call Logging: Centralized function to log call events with duration and status
const handleCallLog = async (status, fromPeer = false) => {
  if (callLoggedRef.current) return;
  callLoggedRef.current = true; // Lock immediately

  let duration = 0;
  if (callStartTimeRef.current) {
    duration = Math.floor((Date.now() - callStartTimeRef.current) / 1000);
  }
  
  if (status === "ended" && !callConnectedRef.current) return;

  const peerId = activePeerIdRef.current || toId;
  if (!peerId || peerId === "undefined" || !myId) return;

  const callEvent = {
    from: fromPeer ? peerId : myId,
    to: fromPeer ? myId : peerId,
    type: "call",
    status: status,
    callType: callTypeRef.current,
    duration: duration,
    timestamp: new Date().toISOString(),
  };
  
  // Show in UI for both Web and App
  setMessages((prev) => [...prev, callEvent]);
  
  // 🛡️ ONLY the Caller saves to the Database! 
  if (!fromPeer) {
    try { 
      if (token) await sendCallEvent({ ...callEvent, token }); 
    } catch (err) {
      console.log("DB Log Error:", err);
    }
  }
};

  const resetCallState = () => {
    stopRingtone();
    clearTimeout(missedCallTimerRef.current);
    endWebRTC({ notifyPeer: false, toId: activePeerIdRef.current });
    setIncomingCall(null);
    setCalling(false);
    setInCall(false);
    activePeerIdRef.current = null;
    callStartTimeRef.current = null;
    callConnectedRef.current = false;
  };

  // 1. Join Room (Only for Chat Messages)
  useEffect(() => {
    if (!myId || !toId) return;
    const currentRoom = [String(myId), String(toId)].sort().join("-");
    if (joinedRoomRef.current === currentRoom) return;
    
    // Only join if different
    socket.emit("joinRoom", { from: myId, to: toId });
    joinedRoomRef.current = currentRoom;
  }, [myId, toId]);

  // 2. Global Call Listeners (CRITICAL FIX: Reduced Dependencies)
  useEffect(() => {
    if (!myId) return;

    const handleIncoming = ({ from, type }) => {
      if (inCall || calling) return; 
      callLoggedRef.current = false;
      activePeerIdRef.current = String(from);
      setIncomingCall({ from, type }); 
      callTypeRef.current = type;
      playRingtone();
    };

   // Inside useChatLogic.js -> useEffect -> handleAccepted
    const handleAccepted = async ({ from }) => {
      if (String(from) !== activePeerIdRef.current) return;
      stopRingtone();
      setCalling(false);
      setInCall(true);
      callConnectedRef.current = true;
      callStartTimeRef.current = Date.now();
      
      // ✅ ENSURE THIS: 1st arg is PeerID, 2nd is Type ("audio" or "video")
      await startWebRTC(activePeerIdRef.current, callTypeRef.current, "caller");
    };

    // Inside useChatLogic.js -> useEffect -> handleOffer
    const handleOffer = async ({ offer, type, from }) => {
      if (String(from) !== activePeerIdRef.current) return;
      
      // ✅ ENSURE THIS: 1st arg is PeerID, 2nd is Type
      await startWebRTC(activePeerIdRef.current, type, "callee", offer);
    };

    const handleAnswer = async ({ answer, from }) => {
      if (String(from) !== activePeerIdRef.current) return;
      await applyAnswer({ answer });
    };

    const handleIce = ({ candidate, from }) => {
      if (String(from) !== activePeerIdRef.current) return;
      applyRemoteIce({ candidate });
    };

    const handleEnd = ({ from }) => {
      if (String(from) !== activePeerIdRef.current) return;
      if (callConnectedRef.current) handleCallLog("ended", true);
      resetCallState();
    };

    const handleCancel = ({ from }) => {
      if (String(from) === activePeerIdRef.current) handleCallLog("missed", true);
      resetCallState();
    };

    const handleRejected = ({ from }) => {
      if (String(from) !== activePeerIdRef.current) return;
      handleCallLog("rejected", true);
      resetCallState();
    };

    const handleMessage = (msg) => setMessages((prev) => [...prev, msg]);
    const handleTyping = () => { setTyping(true); setTimeout(() => setTyping(false), 2000); };

    // Attach Listeners
    socket.on("call:incoming", handleIncoming);
    socket.on("call:accepted", handleAccepted);
    socket.on("call:offer", handleOffer);
    socket.on("call:answer", handleAnswer);
    socket.on("call:ice-candidate", handleIce);
    socket.on("call:end", handleEnd);
    socket.on("call:cancel", handleCancel);
    socket.on("call:rejected", handleRejected);
    socket.on("message", handleMessage);
    socket.on("typing", handleTyping);

    // Cleanup
    return () => {
      socket.off("call:incoming", handleIncoming);
      socket.off("call:accepted", handleAccepted);
      socket.off("call:offer", handleOffer);
      socket.off("call:answer", handleAnswer);
      socket.off("call:ice-candidate", handleIce);
      socket.off("call:end", handleEnd);
      socket.off("call:cancel", handleCancel);
      socket.off("call:rejected", handleRejected);
      socket.off("message", handleMessage);
      socket.off("typing", handleTyping);
    };
    
    
  }, [myId]); 

  // ... (Rest of the file remains same: Load History, Scroll, Actions) ...
  useEffect(() => {
    if (!myId || !toId || !token) return;
    fetchUserChat(toId, token).then((msgs) => setHistory(Array.isArray(msgs) ? msgs : [])).catch(() => setHistory([]));
  }, [myId, toId, token]);

  useEffect(() => {
    requestAnimationFrame(() => { if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight; });
  }, [messages, history]);

  const allMessages = useMemo(() => {
    const h = Array.isArray(history) ? history : [];
    const m = Array.isArray(messages) ? messages : [];
    return [...h, ...m].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }, [history, messages]);

  const sendMessage = () => {
    if (!text.trim()) return;
    socket.emit("sendMessage", { from: myId, to: toId, text, timestamp: new Date().toISOString() });
    setText("");
  };

  const handleTyping = () => socket.emit("typing", { room: [String(myId), String(toId)].sort().join("-") });

  const startCall = (type = "video") => {
    if (!toId) return;
    callLoggedRef.current = false; 
    callConnectedRef.current = false;
    setCallType(type);
    callTypeRef.current = type;
    setCalling(true);
    playRingtone();
    activePeerIdRef.current = String(toId);
    socket.emit("call:request", { from: myId, to: toId, type });
    missedCallTimerRef.current = setTimeout(() => {
      if (!callConnectedRef.current) {
        socket.emit("call:cancel", { from: myId, to: toId });
        handleCallLog("missed");
        resetCallState();
      }
    }, 30000);
  };

  const acceptCall = () => {
    if (!incomingCall) return;
    callLoggedRef.current = false;
    stopRingtone();
    clearTimeout(missedCallTimerRef.current);
    activePeerIdRef.current = String(incomingCall.from);
    callConnectedRef.current = true;
    callStartTimeRef.current = Date.now();
    setIncomingCall(null);
    setInCall(true);
    socket.emit("call:accept", { from: myId, to: incomingCall.from, type: incomingCall.type });
  };

  const rejectCall = () => {
    if (!incomingCall) return;
    callLoggedRef.current = false; 
    socket.emit("call:reject", { from: myId, to: incomingCall.from });
    resetCallState();
  };

  const endCall = () => {
  const peerId = activePeerIdRef.current || toId;
  
  // 🛡️ Ensure peerId is completely valid before sending
  if (peerId && peerId !== "undefined" && peerId !== "null" && myId) {
    socket.emit("call:end", { from: myId, to: peerId, reason: "ended" });
  }

  if (callConnectedRef.current) handleCallLog("ended");
  else handleCallLog("missed"); 
  
  resetCallState();
}

  return {
    messages, history, text, typing, inCall, calling, incomingCall, showProfile, localStream, remoteStream, chatRef, callType, allMessages,
    setText, setShowProfile, sendMessage, handleTyping, startCall, acceptCall, rejectCall, endCall
  };
}